'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { FileDropzone } from '@/components/data-lab/FileDropzone';
import { BarChart } from '@/components/data-lab/BarChart';
import { LineChart } from '@/components/data-lab/LineChart';
import {
  type DepotDataset,
  type WIPRow,
  type SupplyRow,
  type ScheduleRow,
  parseDepotFile,
  saveDataset,
  loadDataset,
  clearDataset,
  loadSampleDataset,
  countBy,
  groupBy,
} from '@/lib/data-lab/depot-data';

// ── Stat tile ─────────────────────────────────────────────────────────────────

function Stat({
  label,
  value,
  sub,
  color = 'text-slate-900',
  onClick,
}: {
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
  onClick?: () => void;
}) {
  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="bg-white rounded-xl border border-slate-200 px-4 py-3 text-left w-full hover:border-red-300 hover:shadow-sm transition-all group cursor-pointer"
      >
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0.5 group-hover:text-red-400 transition-colors">{label}</p>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
        {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
        <p className="text-[9px] text-slate-300 group-hover:text-red-300 mt-1 transition-colors">click to view →</p>
      </button>
    );
  }
  return (
    <div className="bg-white rounded-xl border border-slate-200 px-4 py-3">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Past ECD Modal ─────────────────────────────────────────────────────────────

function PastECDModal({ rows, onClose }: { rows: WIPRow[]; onClose: () => void }) {
  const sorted = [...rows].sort((a, b) => parseInt(b.Days_In_WIP) - parseInt(a.Days_In_WIP));
  const today = new Date();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.55)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            <div>
              <p className="text-sm font-bold text-slate-900">Past ECD — All Open Jobs</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{sorted.length} work orders past estimated completion date</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 transition-colors p-1 rounded-lg hover:bg-slate-100"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Modal body */}
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-slate-50 z-10">
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 font-semibold text-slate-500">WO</th>
                <th className="text-left py-2 px-3 font-semibold text-slate-500">Platform</th>
                <th className="text-left py-2 px-3 font-semibold text-slate-500">End Item</th>
                <th className="text-left py-2 px-3 font-semibold text-slate-500">Status</th>
                <th className="text-left py-2 px-3 font-semibold text-slate-500">ECD</th>
                <th className="text-right py-2 px-3 font-semibold text-slate-500">Days Over</th>
                <th className="text-right py-2 px-3 font-semibold text-slate-500">%</th>
                <th className="text-right py-2 px-3 font-semibold text-slate-500">Days WIP</th>
                <th className="text-left py-2 px-3 font-semibold text-slate-500">Lead</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => {
                const ecd = new Date(r.ECD);
                const daysOver = isNaN(ecd.getTime()) ? null : Math.floor((today.getTime() - ecd.getTime()) / 86_400_000);
                return (
                  <tr key={r.WO_Number} className="border-b border-slate-50 hover:bg-red-50/30 transition-colors">
                    <td className="py-2 px-3 font-mono text-slate-700">{r.WO_Number}</td>
                    <td className="py-2 px-3 text-slate-600">{r.Platform}</td>
                    <td className="py-2 px-3 text-slate-600 max-w-[140px] truncate" title={r.End_Item}>{r.End_Item}</td>
                    <td className="py-2 px-3">
                      <span className={`px-1.5 py-0.5 rounded border font-medium ${
                        r.AWP_Flag === 'Y'
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : r.Priority === 'EXREP'
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : 'bg-slate-50 text-slate-600 border-slate-200'
                      }`}>
                        {r.Status}
                      </span>
                    </td>
                    <td className="py-2 px-3 font-mono text-slate-500">{r.ECD}</td>
                    <td className="py-2 px-3 text-right font-mono font-semibold text-red-600">
                      {daysOver !== null ? `+${daysOver}d` : '—'}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-slate-600">{r.Percent_Complete}%</td>
                    <td className="py-2 px-3 text-right font-mono font-semibold text-red-600">{r.Days_In_WIP}</td>
                    <td className="py-2 px-3 text-slate-500">{r.Lead_Artisan}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Modal footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
          <p className="text-[10px] text-slate-400">Sorted by Days in WIP (highest first) · RFI jobs included</p>
          <button
            onClick={onClose}
            className="text-xs font-semibold px-4 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Section header ─────────────────────────────────────────────────────────────

function SectionHeader({ title, count }: { title: string; count: number }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{title}</p>
      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
        {count}
      </span>
    </div>
  );
}

// ── WIP Dashboard ─────────────────────────────────────────────────────────────

function WIPDashboard({ rows }: { rows: WIPRow[] }) {
  const [pastECDOpen, setPastECDOpen] = useState(false);

  const statusCounts = countBy(rows, 'Status');
  const shopCounts = countBy(rows, 'Shop_Cost_Center').map((d) => ({
    ...d,
    label: d.label.replace(/^\d+\s+/, ''), // strip leading cost-center number
  }));
  const platformCounts = countBy(rows, 'Platform');

  const pastECD = rows.filter((r) => r.Past_ECD === 'Y');
  const awpJobs = rows.filter((r) => r.AWP_Flag === 'Y');
  const exrep = rows.filter((r) => r.Priority === 'EXREP');
  const rfi = rows.filter((r) => r.Status === 'RFI');

  const pastECDNotRFI = pastECD
    .filter((r) => r.Status !== 'RFI')
    .sort((a, b) => parseInt(b.Days_In_WIP) - parseInt(a.Days_In_WIP))
    .slice(0, 10);

  return (
    <div className="space-y-5">
      {pastECDOpen && <PastECDModal rows={pastECD} onClose={() => setPastECDOpen(false)} />}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Total WOs" value={rows.length} />
        <Stat label="Past ECD" value={pastECD.length} sub="not yet closed" color="text-red-600" onClick={() => setPastECDOpen(true)} />
        <Stat label="AWP Hold" value={awpJobs.length} sub="awaiting parts" color="text-amber-600" />
        <Stat label="EXREP" value={exrep.length} sub={`${rfi.length} RFI`} color="text-indigo-700" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {/* Status breakdown */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <SectionHeader title="By Status" count={statusCounts.length} />
          <BarChart data={statusCounts} color="#6366f1" />
        </div>

        {/* Platform breakdown */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <SectionHeader title="By Platform" count={platformCounts.length} />
          <BarChart data={platformCounts} color="#0891b2" />
        </div>
      </div>

      {/* Shop breakdown */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <SectionHeader title="By Shop / Cost Center" count={shopCounts.length} />
        <BarChart data={shopCounts} color="#059669" />
      </div>

      {/* Past-ECD, not RFI */}
      {pastECDNotRFI.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <SectionHeader title="Past ECD — Not RFI (top 10 by Days in WIP)" count={pastECDNotRFI.length} />
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-1.5 px-2 font-semibold text-slate-500">WO</th>
                  <th className="text-left py-1.5 px-2 font-semibold text-slate-500">Platform</th>
                  <th className="text-left py-1.5 px-2 font-semibold text-slate-500">End Item</th>
                  <th className="text-left py-1.5 px-2 font-semibold text-slate-500">Status</th>
                  <th className="text-right py-1.5 px-2 font-semibold text-slate-500">%</th>
                  <th className="text-right py-1.5 px-2 font-semibold text-slate-500">Days WIP</th>
                </tr>
              </thead>
              <tbody>
                {pastECDNotRFI.map((r) => (
                  <tr key={r.WO_Number} className="border-b border-slate-50 hover:bg-red-50/30 transition-colors">
                    <td className="py-1.5 px-2 font-mono text-slate-700">{r.WO_Number}</td>
                    <td className="py-1.5 px-2 text-slate-600">{r.Platform}</td>
                    <td className="py-1.5 px-2 text-slate-600">{r.End_Item}</td>
                    <td className="py-1.5 px-2">
                      <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-medium">
                        {r.Status}
                      </span>
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono text-slate-700">{r.Percent_Complete}%</td>
                    <td className="py-1.5 px-2 text-right font-mono text-red-600 font-semibold">{r.Days_In_WIP}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Supply / AWP Dashboard ─────────────────────────────────────────────────────

function SupplyDashboard({ rows }: { rows: SupplyRow[] }) {
  const statusCounts = countBy(rows, 'Supply_Status');
  const sourceCounts = countBy(rows, 'Source');
  const backordered = rows.filter((r) => r.Supply_Status === 'Backordered');
  const soleSource = rows.filter((r) => r.Source.includes('OEM-Sole Source'));
  const pastEDD = rows.filter((r) => r.Past_EDD === 'Y');

  const criticalHolds = rows
    .filter((r) => r.Source.includes('OEM-Sole Source') && r.Supply_Status === 'Backordered')
    .sort((a, b) => parseInt(b.Lead_Time_Days) - parseInt(a.Lead_Time_Days));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Total Lines" value={rows.length} />
        <Stat label="Backordered" value={backordered.length} color="text-red-600" />
        <Stat label="Sole-Source OEM" value={soleSource.length} color="text-amber-600" sub="no alternates" />
        <Stat label="Past EDD" value={pastEDD.length} color="text-red-600" sub="overdue receipt" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <SectionHeader title="By Supply Status" count={statusCounts.length} />
          <BarChart data={statusCounts} color="#dc2626" />
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <SectionHeader title="By Source" count={sourceCounts.length} />
          <BarChart data={sourceCounts} color="#d97706" />
        </div>
      </div>

      {criticalHolds.length > 0 && (
        <div className="bg-white rounded-xl border border-red-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <SectionHeader title="Critical: Backordered Sole-Source OEM Parts" count={criticalHolds.length} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-1.5 px-2 font-semibold text-slate-500">WO</th>
                  <th className="text-left py-1.5 px-2 font-semibold text-slate-500">Platform</th>
                  <th className="text-left py-1.5 px-2 font-semibold text-slate-500">Part</th>
                  <th className="text-right py-1.5 px-2 font-semibold text-slate-500">BO Qty</th>
                  <th className="text-right py-1.5 px-2 font-semibold text-slate-500">Lead (days)</th>
                  <th className="text-left py-1.5 px-2 font-semibold text-slate-500">EDD</th>
                </tr>
              </thead>
              <tbody>
                {criticalHolds.map((r) => (
                  <tr key={r.Line_ID} className="border-b border-slate-50 hover:bg-red-50/30 transition-colors">
                    <td className="py-1.5 px-2 font-mono text-slate-700">{r.WO_Number}</td>
                    <td className="py-1.5 px-2 text-slate-600">{r.Platform}</td>
                    <td className="py-1.5 px-2 text-slate-600 max-w-[160px] truncate">{r.Part_Nomenclature}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-red-600 font-semibold">{r.Backorder_Qty}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-amber-700 font-semibold">{r.Lead_Time_Days}</td>
                    <td className="py-1.5 px-2 text-slate-500 font-mono">{r.EDD}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Schedule Dashboard ─────────────────────────────────────────────────────────

function ScheduleDashboard({ rows }: { rows: ScheduleRow[] }) {
  // Aggregate by month across all product lines
  const byMonth = new Map<string, { planned: number; actual: number }>();
  for (const r of rows) {
    const existing = byMonth.get(r.Month) ?? { planned: 0, actual: 0 };
    existing.planned += parseInt(r.Planned_Output_Units) || 0;
    existing.actual += parseInt(r.Actual_Output_Units) || 0;
    byMonth.set(r.Month, existing);
  }
  const monthData = Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([Month, v]) => ({ Month, ...v }));

  // TAT by product line — average Actual vs Target
  const byLine = groupBy(rows, 'Product_Line');
  const tatByLine = Array.from(byLine.entries()).map(([line, lineRows]) => {
    const avgActual = lineRows.reduce((s, r) => s + (parseFloat(r.Actual_TAT_Days) || 0), 0) / lineRows.length;
    const avgTarget = lineRows.reduce((s, r) => s + (parseFloat(r.Target_TAT_Days) || 0), 0) / lineRows.length;
    const avgAdherence = lineRows.reduce((s, r) => s + (parseFloat(r.Schedule_Adherence_Pct) || 0), 0) / lineRows.length;
    return { line, avgActual, avgTarget, avgAdherence };
  }).sort((a, b) => b.avgActual - a.avgActual);

  const totalPlanned = monthData.reduce((s, d) => s + d.planned, 0);
  const totalActual = monthData.reduce((s, d) => s + d.actual, 0);
  const hitRate = totalPlanned > 0 ? ((totalActual / totalPlanned) * 100).toFixed(0) : '—';

  const productLines = Array.from(byLine.keys());

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Product Lines" value={productLines.length} />
        <Stat label="Planned (12 mo)" value={totalPlanned} sub="total units" />
        <Stat label="Actual (12 mo)" value={totalActual} sub="units delivered" color={totalActual >= totalPlanned ? 'text-emerald-600' : 'text-red-600'} />
        <Stat label="Plan Attainment" value={`${hitRate}%`} color={parseInt(hitRate) >= 90 ? 'text-emerald-600' : 'text-red-600'} />
      </div>

      {/* Throughput trend */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <SectionHeader title="Monthly Throughput — All Lines" count={monthData.length} />
        <LineChart
          data={monthData}
          xKey="Month"
          series={[
            { key: 'planned', label: 'Planned', color: '#94a3b8' },
            { key: 'actual', label: 'Actual', color: '#6366f1' },
          ]}
          height={140}
        />
        <div className="flex items-center gap-4 mt-2 justify-center">
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
            <span className="w-4 h-0.5 bg-slate-400 inline-block" /> Planned
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
            <span className="w-4 h-0.5 bg-indigo-500 inline-block" /> Actual
          </div>
        </div>
      </div>

      {/* TAT and adherence by product line */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <SectionHeader title="Avg TAT vs Target by Product Line (days)" count={tatByLine.length} />
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-1.5 px-2 font-semibold text-slate-500">Product Line</th>
                <th className="text-right py-1.5 px-2 font-semibold text-slate-500">Target TAT</th>
                <th className="text-right py-1.5 px-2 font-semibold text-slate-500">Actual TAT</th>
                <th className="text-right py-1.5 px-2 font-semibold text-slate-500">Delta</th>
                <th className="text-right py-1.5 px-2 font-semibold text-slate-500">Avg Adherence</th>
              </tr>
            </thead>
            <tbody>
              {tatByLine.map(({ line, avgActual, avgTarget, avgAdherence }) => {
                const delta = avgActual - avgTarget;
                return (
                  <tr key={line} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <td className="py-1.5 px-2 font-medium text-slate-700">{line}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-slate-500">{avgTarget.toFixed(0)}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-slate-700 font-semibold">{avgActual.toFixed(1)}</td>
                    <td className={`py-1.5 px-2 text-right font-mono font-semibold ${delta > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {delta > 0 ? '+' : ''}{delta.toFixed(1)}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono text-slate-700">{avgAdherence.toFixed(0)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Upload status indicator ───────────────────────────────────────────────────

function FileStatus({
  label,
  loaded,
}: {
  label: string;
  loaded: boolean;
}) {
  return (
    <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border ${loaded ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-400'}`}>
      <span className={`w-2 h-2 rounded-full ${loaded ? 'bg-emerald-500' : 'bg-slate-300'}`} />
      {label}
    </div>
  );
}

// ── Tab button ─────────────────────────────────────────────────────────────────

function TabBtn({
  active,
  onClick,
  children,
  count,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  count?: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
        active
          ? 'bg-indigo-600 text-white shadow-sm'
          : 'bg-white text-slate-600 border border-slate-200 hover:border-indigo-200 hover:text-indigo-700'
      }`}
    >
      {children}
      {count !== undefined && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${active ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

type Tab = 'wip' | 'supply' | 'schedule';

export default function DataLabPage() {
  const [dataset, setDataset] = useState<DepotDataset>({ wip: null, supply: null, schedule: null });
  const [loading, setSampleLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('wip');
  const [hydrated, setHydrated] = useState(false);

  // Rehydrate from localStorage on mount
  useEffect(() => {
    const saved = loadDataset();
    if (saved) setDataset(saved);
    setHydrated(true);
  }, []);

  const hasAny = dataset.wip || dataset.supply || dataset.schedule;

  const handleFiles = useCallback(async (files: File[]) => {
    const next = { ...dataset };
    await Promise.all(
      files.map(async (file) => {
        const text = await file.text();
        const { type, rows } = parseDepotFile(text);
        if (type === 'wip') { next.wip = rows; setActiveTab('wip'); }
        if (type === 'supply') { next.supply = rows; }
        if (type === 'schedule') { next.schedule = rows; }
      })
    );
    setDataset(next);
    saveDataset(next);
    setLoadError(null);
  }, [dataset]);

  const handleLoadSample = async () => {
    setSampleLoading(true);
    setLoadError(null);
    try {
      const ds = await loadSampleDataset();
      setDataset(ds);
      saveDataset(ds);
      setActiveTab('wip');
    } catch {
      setLoadError('Failed to load sample data. Make sure the CSV files are in /public/downloads/.');
    } finally {
      setSampleLoading(false);
    }
  };

  const handleClear = () => {
    clearDataset();
    setDataset({ wip: null, supply: null, schedule: null });
  };

  const autoTab: Tab = dataset.wip ? 'wip' : dataset.supply ? 'supply' : 'schedule';
  const displayTab = hasAny ? activeTab : autoTab;

  if (!hydrated) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="h-1 bg-gradient-to-r from-indigo-700 via-indigo-500 to-purple-500" />

      {/* Header */}
      <header className="border-b border-slate-200 bg-white px-6 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Home
          </Link>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-[10px] text-slate-400 border border-slate-200 rounded px-2 py-1">
              All data stays in your browser
            </span>
            {hasAny && (
              <button
                onClick={handleClear}
                className="text-[10px] text-slate-400 hover:text-red-500 border border-slate-200 hover:border-red-200 rounded px-2 py-1 transition-colors"
              >
                Clear data
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-200 flex items-center justify-center shrink-0">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
                <line x1="2" y1="20" x2="22" y2="20" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200">Level 3</span>
                <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded" style={{ background: 'rgba(79,70,229,0.1)', color: '#4338ca', border: '1px solid rgba(79,70,229,0.2)' }}>LIVE DEMO</span>
              </div>
              <h1 className="text-xl font-bold text-slate-900 leading-tight mb-1">Depot Data Lab</h1>
              <p className="text-sm text-slate-500 leading-relaxed max-w-2xl">
                Upload the three notional depot CSVs and get instant dashboards — WIP status by shop and platform, AWP parts-hold analysis, and 12-month throughput trends.
                All parsing happens in your browser. Nothing is sent to a server.
              </p>
            </div>
          </div>

          {/* File status row */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <FileStatus label="Depot WIP" loaded={!!dataset.wip} />
            <FileStatus label="Supply / AWP" loaded={!!dataset.supply} />
            <FileStatus label="Schedule & Throughput" loaded={!!dataset.schedule} />
            <div className="ml-auto">
              <button
                onClick={handleLoadSample}
                disabled={loading}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-700 hover:bg-indigo-50 transition-colors font-semibold disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <span className="w-3 h-3 border border-indigo-400 border-t-transparent rounded-full animate-spin" />
                    Loading…
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M3 14h10M8 2v8m-3-3 3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Load notional sample data
                  </>
                )}
              </button>
            </div>
          </div>
          {loadError && (
            <p className="mt-2 text-xs text-red-600">{loadError}</p>
          )}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {!hasAny ? (
          /* ── Upload state ─────────────────────────────────────────────────── */
          <div className="space-y-4">
            <FileDropzone onFiles={handleFiles} disabled={loading} />

            <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">Which tool for which data?</p>
              <div className="grid sm:grid-cols-3 gap-3 text-[11px]">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                  <p className="font-bold text-emerald-800 mb-1">Notional / Non-CUI</p>
                  <p className="text-emerald-700">These training files — any LLM (ChatGPT, Claude, Gemini) is fine.</p>
                </div>
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                  <p className="font-bold text-blue-800 mb-1">CUI / FOUO</p>
                  <p className="text-blue-700">Real NALCOMIS exports, BuNos, maintenance records — GenAI.mil (IL5) only, or depersonalize first.</p>
                </div>
                <div className="rounded-xl border border-red-200 bg-red-50 p-3">
                  <p className="font-bold text-red-800 mb-1">Classified</p>
                  <p className="text-red-700">SIPR content, COMSEC, OpPlans — no current IL5 AI tool. SCIF only.</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ── Dashboard state ──────────────────────────────────────────────── */
          <>
            {/* Tab row */}
            <div className="flex items-center gap-2 flex-wrap">
              {dataset.wip && (
                <TabBtn active={displayTab === 'wip'} onClick={() => setActiveTab('wip')} count={dataset.wip.length}>
                  WIP Status
                </TabBtn>
              )}
              {dataset.supply && (
                <TabBtn active={displayTab === 'supply'} onClick={() => setActiveTab('supply')} count={dataset.supply.length}>
                  Supply / AWP
                </TabBtn>
              )}
              {dataset.schedule && (
                <TabBtn active={displayTab === 'schedule'} onClick={() => setActiveTab('schedule')} count={dataset.schedule.length}>
                  Throughput
                </TabBtn>
              )}
              <div className="ml-auto">
                <FileDropzone onFiles={handleFiles} />
              </div>
            </div>

            {/* Dashboard panels */}
            {displayTab === 'wip' && dataset.wip && (
              <WIPDashboard rows={dataset.wip as WIPRow[]} />
            )}
            {displayTab === 'supply' && dataset.supply && (
              <SupplyDashboard rows={dataset.supply as SupplyRow[]} />
            )}
            {displayTab === 'schedule' && dataset.schedule && (
              <ScheduleDashboard rows={dataset.schedule as ScheduleRow[]} />
            )}

            {/* Schedule board callout */}
            {dataset.wip && (
              <div className="rounded-2xl border border-violet-100 bg-violet-50/50 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <p className="text-xs font-bold text-violet-700 mb-1">Explore your work center load</p>
                  <p className="text-[11px] text-violet-600 leading-relaxed">
                    Open the interactive Schedule Board to drag work orders between shops, spot imbalances, and visualize what a rebalanced schedule could look like.
                  </p>
                </div>
                <Link
                  href="/schedule-board"
                  className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' }}
                >
                  Open Schedule Board
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
              </div>
            )}

            {/* Prompt arc callout */}
            <div className="rounded-2xl border border-indigo-100 bg-indigo-50/50 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <p className="text-xs font-bold text-indigo-700 mb-1">Ready for the full prompt arc?</p>
                <p className="text-[11px] text-indigo-600 leading-relaxed">
                  Download these CSVs and upload them to GenAI.mil or your LLM — then run the Level 2 morning huddle prompts to generate a full production brief.
                </p>
              </div>
              <Link
                href="/scenarios/depot-morning-huddle"
                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #4338ca 0%, #3730a3 100%)' }}
              >
                Morning Huddle Scenario
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </div>
          </>
        )}
      </main>

      <footer className="border-t border-slate-200 py-4 px-6 text-center text-[10px] text-slate-400">
        AIBMM Depot Data Lab · All data is notional — training use only · No data is sent to any server
        <span className="mx-2">·</span>
        <Link href="/" className="hover:text-slate-600 transition-colors">Home</Link>
      </footer>
    </div>
  );
}
